import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Trash2, Pencil, X, GripVertical, ListChecks, Zap } from 'lucide-react';
import { taskTemplatesApi, type TaskTemplateInput } from '../../api/taskTemplates';
import { productLinesApi } from '../../api/productLines';
import { companiesApi } from '../../api/companies';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfirm } from '../ConfirmDialog';
import EmptyState from '../EmptyState';
import { useClassifications } from '../../hooks/useClassifications';
import TaskAssigneeSelect from '../tickets/TaskAssigneeSelect';
import type { TaskTemplate } from '../../types';

/* Company Settings → Task templates: the default sub-ticket checklists.
   Each template names the tasks a ticket of a given type and/or product
   line starts with (auto-apply), and every template is offered as "Add from
   template" on any ticket. */

// The ticket-type list is per-company (Company Settings -> Classification);
// "Any type" is this control's own sentinel, not a classification value.
const ANY_TYPE = { key: '', label: 'Any type' };

interface ItemForm { key: string; id?: string; title: string; description: string; defaultAssigneeId: string }
interface FormState {
  name: string;
  description: string;
  ticketType: string;
  productLineId: string;
  autoApply: boolean;
  isActive: boolean;
  items: ItemForm[];
}

let keySeq = 0;
const newKey = () => `k${++keySeq}`;
const blankItem = (): ItemForm => ({ key: newKey(), title: '', description: '', defaultAssigneeId: '' });
const EMPTY: FormState = { name: '', description: '', ticketType: '', productLineId: '', autoApply: true, isActive: true, items: [blankItem()] };

function toForm(t: TaskTemplate): FormState {
  return {
    name: t.name,
    description: t.description ?? '',
    ticketType: t.ticketType ?? '',
    productLineId: t.productLineId ?? '',
    autoApply: t.autoApply,
    isActive: t.isActive,
    items: t.items.length > 0
      ? t.items.map(i => ({ key: newKey(), id: i.id, title: i.title, description: i.description ?? '', defaultAssigneeId: i.defaultAssigneeId ?? '' }))
      : [blankItem()],
  };
}

function toInput(f: FormState): TaskTemplateInput {
  return {
    name: f.name.trim(),
    description: f.description.trim() || null,
    ticketType: f.ticketType || null,
    productLineId: f.productLineId || null,
    autoApply: f.autoApply,
    isActive: f.isActive,
    items: f.items
      .filter(i => i.title.trim())
      .map(i => ({ ...(i.id ? { id: i.id } : {}), title: i.title.trim(), description: i.description.trim() || null, defaultAssigneeId: i.defaultAssigneeId || null })),
  };
}

export default function TaskTemplatesTab() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { currentCompany } = useCompany();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const [editingId, setEditingId] = useState<string | null>(null);   // 'new' for a fresh one
  const [form, setForm] = useState<FormState>(EMPTY);

  // A template can target a type the company has since retired, so keep the
  // saved value in the list even when it is no longer offered.
  const { optionsFor } = useClassifications();
  const typeOptions = [ANY_TYPE, ...optionsFor('type', form.ticketType)];

  const { data: templates = [], isLoading } = useQuery({ queryKey: ['task-templates'], queryFn: () => taskTemplatesApi.list(), enabled: !!currentCompany });
  const { data: productLines = [] } = useQuery({ queryKey: ['product-lines'], queryFn: () => productLinesApi.list(), enabled: !!currentCompany });
  const { data: members = [] } = useQuery({ queryKey: ['company-members', companyId], queryFn: () => companiesApi.getMembers(companyId), enabled: !!companyId });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['task-templates'] });
  const onError = (e: any) => toast.error(e?.response?.data?.message ?? e.message);

  const createMutation = useMutation({
    mutationFn: () => taskTemplatesApi.create(toInput(form)),
    onSuccess: () => { invalidate(); setEditingId(null); setForm(EMPTY); toast.success('Template created'); },
    onError,
  });
  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; data: Partial<TaskTemplateInput> }) => taskTemplatesApi.update(vars.id, vars.data),
    onSuccess: () => { invalidate(); setEditingId(null); toast.success('Template saved'); },
    onError,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => taskTemplatesApi.remove(id),
    onSuccess: () => { invalidate(); toast.success('Template deleted'); },
    onError,
  });

  const startNew = () => { setForm({ ...EMPTY, items: [blankItem()] }); setEditingId('new'); };
  const startEdit = (t: TaskTemplate) => { setForm(toForm(t)); setEditingId(t.id); };
  const cancel = () => { setEditingId(null); setForm(EMPTY); };

  const setItem = (key: string, patch: Partial<ItemForm>) =>
    setForm(f => ({ ...f, items: f.items.map(i => (i.key === key ? { ...i, ...patch } : i)) }));
  const removeItem = (key: string) => setForm(f => ({ ...f, items: f.items.filter(i => i.key !== key) }));
  const moveItem = (key: string, dir: -1 | 1) => setForm(f => {
    const idx = f.items.findIndex(i => i.key === key);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= f.items.length) return f;
    const items = [...f.items];
    [items[idx], items[to]] = [items[to], items[idx]];
    return { ...f, items };
  });

  const canSave = form.name.trim().length > 0 && form.items.some(i => i.title.trim());
  const save = () => {
    if (!canSave) { toast.error('Give the template a name and at least one task'); return; }
    if (editingId === 'new') createMutation.mutate();
    else if (editingId) updateMutation.mutate({ id: editingId, data: toInput(form) });
  };
  const saving = createMutation.isPending || updateMutation.isPending;

  const lineName = (id?: string | null) => (productLines as any[]).find(p => p.id === id)?.name ?? null;
  const typeLabel = (v?: string | null) => typeOptions.find(o => o.key === (v ?? ''))?.label ?? v;
  const memberName = (id?: string | null) => (members as any[]).find(m => m.user?.id === id)?.user?.name ?? null;

  const editor = (
    <div className="card p-5 space-y-4" style={{ borderColor: 'var(--scpa-primary)' }}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="label" htmlFor="tt-name">Template name</label>
          <input id="tt-name" className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Agency onboarding" autoFocus />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor="tt-desc">Description (optional)</label>
          <input id="tt-desc" className="input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="When to use this checklist" />
        </div>
        <div>
          <label className="label" htmlFor="tt-type">Applies to ticket type</label>
          <select id="tt-type" className="select" value={form.ticketType} onChange={e => setForm(f => ({ ...f, ticketType: e.target.value }))}>
            {typeOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="tt-line">Applies to product line</label>
          <select id="tt-line" className="select" value={form.productLineId} onChange={e => setForm(f => ({ ...f, productLineId: e.target.value }))}>
            <option value="">Any product line</option>
            {(productLines as any[]).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <label className="flex items-start gap-2 cursor-pointer text-sm">
          <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[var(--scpa-primary)]" checked={form.autoApply} onChange={e => setForm(f => ({ ...f, autoApply: e.target.checked }))} />
          <span>
            <span className="font-medium text-fg">Add automatically to new tickets that match</span>
            <span className="block text-xs text-muted">The most specific matching template wins: type + product line beats product line alone beats type alone beats "any". Off = offered under "From template" only.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer text-sm">
          <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[var(--scpa-primary)]" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
          <span>
            <span className="font-medium text-fg">Active</span>
            <span className="block text-xs text-muted">Inactive templates are kept but never applied or offered.</span>
          </span>
        </label>
      </div>

      <div>
        <p className="label">Tasks, in order</p>
        <div className="space-y-2">
          {form.items.map((item, idx) => (
            <div key={item.key} className="flex items-start gap-2 rounded-lg border border-line-light p-2">
              <div className="flex flex-col items-center gap-0.5 pt-1.5 text-muted">
                <GripVertical className="w-3.5 h-3.5" />
                <button type="button" className="text-[10px] hover:text-fg" onClick={() => moveItem(item.key, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Move up">▲</button>
                <button type="button" className="text-[10px] hover:text-fg" onClick={() => moveItem(item.key, 1)} disabled={idx === form.items.length - 1} style={{ background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Move down">▼</button>
              </div>
              <div className="flex-1 min-w-0 grid gap-1.5 sm:grid-cols-[1fr_1fr_12rem]">
                <input className="input text-sm" value={item.title} onChange={e => setItem(item.key, { title: e.target.value })} placeholder={`Task ${idx + 1} title`} aria-label={`Task ${idx + 1} title`} />
                <input className="input text-sm" value={item.description} onChange={e => setItem(item.key, { description: e.target.value })} placeholder="Details (optional)" aria-label={`Task ${idx + 1} details`} />
                <TaskAssigneeSelect value={item.defaultAssigneeId} members={members as any[]} onChange={v => setItem(item.key, { defaultAssigneeId: v })} className="select text-sm" />
              </div>
              <button type="button" className="text-muted hover:text-err mt-2" onClick={() => removeItem(item.key)} title="Remove task" aria-label={`Remove task ${idx + 1}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0 }}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="btn btn-ghost text-xs mt-2" onClick={() => setForm(f => ({ ...f, items: [...f.items, blankItem()] }))}>
          <Plus className="w-3.5 h-3.5" /> Add task
        </button>
        <p className="text-[11px] text-muted mt-1">A default assignee makes that task theirs the moment the ticket is created (they get the usual "task assigned" email).</p>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn btn-ghost" onClick={cancel} disabled={saving}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={!canSave || saving}>{saving ? 'Saving…' : editingId === 'new' ? 'Create template' : 'Save changes'}</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="section-title">Task templates</h2>
          <p className="text-sm text-muted max-w-2xl">
            Default sub-ticket checklists. A ticket that matches a template's type and/or product line starts with
            these tasks; any template can also be added to any ticket from its Tasks card.
          </p>
        </div>
        {editingId !== 'new' && (
          <button type="button" className="btn btn-primary" onClick={startNew}><Plus className="w-4 h-4" /> New template</button>
        )}
      </div>

      {editingId === 'new' && editor}

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (templates as TaskTemplate[]).length === 0 && editingId !== 'new' ? (
        <div className="card"><EmptyState icon={<ListChecks className="w-8 h-8" />}>No task templates yet. Use <b>New template</b> to define the tasks a project or request should start with.</EmptyState></div>
      ) : (
        <div className="space-y-3">
          {(templates as TaskTemplate[]).map(t => (
            editingId === t.id ? <div key={t.id}>{editor}</div> : (
              <div key={t.id} className="card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-fg flex items-center gap-2 flex-wrap">
                      {t.name}
                      {t.autoApply && <span className="badge text-[10px] inline-flex items-center gap-1" style={{ background: 'var(--scpa-primary-muted)', color: 'var(--scpa-primary)' }}><Zap className="w-3 h-3" /> auto</span>}
                      {!t.isActive && <span className="badge text-[10px] bg-row-odd text-muted">inactive</span>}
                    </p>
                    <p className="text-xs text-muted mt-0.5">
                      {typeLabel(t.ticketType)} · {lineName(t.productLineId) ?? 'Any product line'} · {t.items.length} task{t.items.length === 1 ? '' : 's'}
                    </p>
                    {t.description && <p className="text-xs text-muted mt-1">{t.description}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" className="btn btn-ghost text-xs py-1 px-2" onClick={() => startEdit(t)}><Pencil className="w-3.5 h-3.5" /> Edit</button>
                    <button
                      type="button"
                      className="btn btn-ghost text-xs py-1 px-2 text-err"
                      onClick={async () => {
                        if (await confirm({ title: 'Delete this template?', message: `"${t.name}" is removed. Tasks already added to tickets are kept.`, confirmLabel: 'Delete', danger: true })) {
                          deleteMutation.mutate(t.id);
                        }
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <ol className="mt-3 space-y-1 text-sm">
                  {t.items.map((i, idx) => (
                    <li key={i.id} className="flex items-start gap-2">
                      <span className="text-xs text-muted w-4 text-right shrink-0 mt-0.5">{idx + 1}.</span>
                      <span className="min-w-0">
                        <span className="text-fg">{i.title}</span>
                        {memberName(i.defaultAssigneeId) && <span className="text-xs text-muted"> → {memberName(i.defaultAssigneeId)}</span>}
                        {i.description && <span className="block text-xs text-muted">{i.description}</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
